import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, FileText, Scale } from 'lucide-react';

export const TermsOfService = () => {
  const navigate = useNavigate();
  return (
    <div className="max-w-4xl mx-auto p-6 md:p-12 space-y-8 text-gray-800">
      <button 
        id="btn-terms-back"
        onClick={() => navigate(-1)}
        className="flex items-center text-gray-500 hover:text-gray-900 mb-6 font-bold text-xs uppercase tracking-wider cursor-pointer"
      >
        <ArrowLeft size={16} className="mr-2" />
        Go Back
      </button>

      <div className="border-b border-gray-200 pb-6 space-y-2">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 border border-blue-100 rounded-full text-[10px] font-black uppercase tracking-widest text-blue-600">
          <Scale size={12} />
          Legal Agreement
        </div>
        <h1 className="text-3xl md:text-4xl font-black text-gray-900 tracking-tight uppercase italic">Terms of Service</h1>
        <p className="text-xs text-gray-500 font-medium">Last Updated: August 26, 2026</p>
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-black text-gray-900 uppercase italic">1. Acceptance of Terms & Entity Identification</h2>
        <p className="text-sm leading-relaxed text-gray-600">
          These Terms of Service ("Terms") constitute a legally binding agreement between you ("Customer," "User," or "you") and <strong>GrowEzi AI</strong>, a d/b/a of <strong>Data Driven Bookkeeping LLC</strong> ("Company," "we," "us," or "our"), governing your access to and use of <strong>TipValue AI</strong> ("Service," "Application," or "Platform").
        </p>
        <p className="text-sm leading-relaxed text-gray-600">
          By creating an account, accessing, or using TipValue AI, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you are entering into these Terms on behalf of a company, restaurant, or other legal entity, you represent that you have the authority to bind such entity.
        </p>
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-black text-gray-900 uppercase italic">2. Description of Service & Administrative Role</h2>
        <p className="text-sm leading-relaxed text-gray-600">
          TipValue AI provides hospitality operators, managers, and staff with administrative software tools for Point of Sale (POS) tip reconciliation, shift allocation mathematical modeling, employee digital sign-off workflows, dispute tracking, and payroll export formatting.
        </p>
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-black text-gray-900 uppercase italic">3. No Legal, Tax, or Accounting Advice</h2>
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl text-amber-900 text-sm leading-relaxed">
          <p className="font-bold mb-1">Administrative & Computational Nature of Platform:</p>
          <p>
            TipValue AI is an administrative calculation and record-keeping software platform. Neither GrowEzi AI nor Data Driven Bookkeeping LLC provides legal, labor, tax, payroll, or accounting advice through the Service. Customers maintain sole responsibility for ensuring that their tip distribution models, tip-pooling agreements, manager eligibility classifications, credit card fee offsets, and IRS Form 8027 reporting comply fully with all applicable federal (including FLSA), state, and local labor and tax laws.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-black text-gray-900 uppercase italic">4. Account Responsibility & Customer Data</h2>
        <p className="text-sm leading-relaxed text-gray-600">
          You are responsible for maintaining the confidentiality of your login credentials and for all actions taken under your tenant account. You represent and warrant that you have all necessary rights, consents, and authorizations to import employee timecard, sales, and gratuity data into TipValue AI from your connected POS and payroll systems.
        </p>
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-black text-gray-900 uppercase italic">5. Disclaimer of Warranties ("AS IS" / "AS AVAILABLE")</h2>
        <div className="p-5 bg-gray-50 border border-gray-200 rounded-2xl text-gray-700 text-xs leading-relaxed font-mono uppercase space-y-2">
          <p className="font-bold text-gray-900">
            THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND.
          </p>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, GROWEZI AI AND DATA DRIVEN BOOKKEEPING LLC EXPRESSLY DISCLAIM ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING WITHOUT LIMITATION ANY IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, NON-INFRINGEMENT, ACCURACY, OR UNINTERRUPTED OR ERROR-FREE OPERATION.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-black text-gray-900 uppercase italic">6. Limitation of Liability</h2>
        <p className="text-sm leading-relaxed text-gray-600">
          To the maximum extent permitted by law, in no event shall GrowEzi AI, Data Driven Bookkeeping LLC, or their officers, directors, employees, or agents be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, goodwill, or labor dispute costs, arising out of or related to your use of or inability to use the Service. In no event shall our total aggregate liability exceed the total amount paid by you to us for the Service during the twelve (12) months immediately preceding the event giving rise to liability.
        </p>
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-black text-gray-900 uppercase italic">7. Governing Law & Dispute Resolution</h2>
        <p className="text-sm leading-relaxed text-gray-600">
          These Terms shall be governed by and construed in accordance with the laws of the State of Delaware (or the State in which Data Driven Bookkeeping LLC maintains its principal place of business), without regard to its conflict of law principles.
        </p>
      </div>

      <div className="space-y-4 border-t border-gray-200 pt-6">
        <h2 className="text-xl font-black text-gray-900 uppercase italic">8. Contact Information</h2>
        <p className="text-sm text-gray-600">
          Questions regarding these Terms should be directed to:
        </p>
        <div className="p-4 bg-gray-50 rounded-xl text-xs text-gray-700 space-y-1">
          <p className="font-bold">GrowEzi AI (d/b/a Data Driven Bookkeeping LLC)</p>
          <p>Email: <a href="mailto:admin@growezy.ai" className="text-blue-600 underline">admin@growezy.ai</a></p>
        </div>
      </div>
    </div>
  );
};
